import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router";
import {
  BarChart3,
  Check,
  ExternalLink,
  Github,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Unplug,
} from "lucide-react";
import { toast } from "sonner";
import type { ReactNode } from "react";

import type { Brand } from "@/components/AppSidebar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocale } from "@/i18n/LocaleProvider";
import { capturePosthogEvent } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import {
  dataChatAPI,
  type DataChatSource,
  type DataSourceProvider,
  type GoogleAnalyticsProperty,
  type SearchConsoleSite,
} from "@/services/api";

type IntegrationFilter = "all" | "connected" | "available";
type IntegrationProvider = DataSourceProvider | "webflow" | "notion" | "github";
type IntegrationState = "connected" | "available" | "action_required" | "soon";
type GoogleConnectProvider = Extract<
  DataSourceProvider,
  "search_console" | "ga4"
>;
type GoogleOAuthStep = "connect" | "grant";

type IntegrationCard = {
  provider: IntegrationProvider;
  name: string;
  description: string;
  state: IntegrationState;
  badge?: string;
  source?: DataChatSource;
  icon: ReactNode;
  iconClassName: string;
  iconTileClassName: string;
  propertyName?: string | null;
};

type IntegrationsPageProps = {
  embedded?: boolean;
};

const FILTERS: IntegrationFilter[] = ["all", "connected", "available"];
const CONNECTABLE_PROVIDERS: GoogleConnectProvider[] = [
  "ga4",
  "search_console",
];

function googleIntegrationName(
  provider: GoogleConnectProvider,
  t: (key: string) => string,
) {
  return provider === "ga4"
    ? t("settings.integrations.ga4.name")
    : t("settings.integrations.gsc.name");
}

function googleIcon(provider: GoogleConnectProvider, className = "h-5 w-5") {
  return provider === "ga4" ? (
    <BarChart3 className={className} />
  ) : (
    <Search className={className} />
  );
}

function ga4AccountKey(property: GoogleAnalyticsProperty) {
  return property.accountName || property.accountDisplayName || "default";
}

function sourceState(source?: DataChatSource): IntegrationState {
  if (!source) return "available";
  if (source.status === "connected") return "connected";
  if (source.status === "action_required") return "action_required";
  return "available";
}

function cardMatchesFilter(card: IntegrationCard, filter: IntegrationFilter) {
  if (filter === "all") return true;
  if (filter === "connected") return card.state === "connected";
  return card.state === "available" || card.state === "action_required";
}

function canUseSearchConsoleSite(site: SearchConsoleSite) {
  return site.permissionLevel !== "siteUnverifiedUser";
}

function hasUsableGoogleGrant(source?: DataChatSource) {
  if (!source || source.authMode !== "google_oauth") return false;
  if (source.status === "connected") return true;
  return (
    source.status === "action_required" &&
    Boolean(source.siteSelectionRequired || source.propertySelectionRequired)
  );
}

function actionCopy(card: IntegrationCard, t: (key: string) => string) {
  if (card.state === "connected") {
    return {
      label: t("settings.integrations.action.manage"),
      tooltip: t("settings.integrations.tooltip.manage"),
      icon: <Settings className="h-4 w-4" />,
      disabled: false,
    };
  }

  if (card.state === "soon") {
    return {
      label: t("settings.integrations.badge.soon"),
      tooltip: t("settings.integrations.tooltip.soon"),
      icon: null,
      disabled: true,
    };
  }

  if (card.provider === "search_console" || card.provider === "ga4") {
    return {
      label: t("settings.integrations.action.connect"),
      tooltip: t("settings.integrations.tooltip.connect"),
      icon: <Plus className="h-4 w-4" />,
      disabled: false,
    };
  }

  return {
    label: t("settings.integrations.action.connect"),
    tooltip: t("settings.integrations.tooltip.connectSoon"),
    icon: <Plus className="h-4 w-4" />,
    disabled: true,
  };
}

export default function IntegrationsPage({
  embedded = false,
}: IntegrationsPageProps) {
  const { currentBrand } = useOutletContext<{ currentBrand: Brand }>();
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<IntegrationFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [selectedSiteUrl, setSelectedSiteUrl] = useState("");
  const [gscSites, setGscSites] = useState<SearchConsoleSite[]>([]);
  const [ga4DialogOpen, setGa4DialogOpen] = useState(false);
  const [selectedGa4PropertyId, setSelectedGa4PropertyId] = useState("");
  const [selectedGa4AccountName, setSelectedGa4AccountName] = useState("");
  const [ga4Properties, setGa4Properties] = useState<GoogleAnalyticsProperty[]>(
    [],
  );
  const [googleOAuthDialog, setGoogleOAuthDialog] = useState<{
    provider: GoogleConnectProvider;
    step: GoogleOAuthStep;
  } | null>(null);
  const [disconnectDialog, setDisconnectDialog] = useState<{
    provider: GoogleConnectProvider;
    name: string;
  } | null>(null);
  const [googleOAuthUrl, setGoogleOAuthUrl] = useState("");
  const googleOAuthWindowRef = useRef<Window | null>(null);
  const processedOAuthReturnRef = useRef("");
  const brandId = currentBrand?.brandId || currentBrand?.id;
  const companyId = currentBrand?.companyId || currentBrand?.company?.id;

  const analyticsContext = useMemo(
    () => ({
      brand_id: brandId,
      company_id: companyId,
      source: embedded
        ? "settings_brand_integrations"
        : "settings_integrations",
    }),
    [brandId, companyId, embedded],
  );

  const sourcesQueryKey = useMemo(
    () => ["data-chat-sources", brandId],
    [brandId],
  );

  const { data: sources = [], isLoading } = useQuery({
    queryKey: sourcesQueryKey,
    queryFn: async () => {
      if (!brandId) return [];
      const response = await dataChatAPI.getSources(brandId);
      return response.sources;
    },
    enabled: Boolean(brandId),
    staleTime: 60_000,
  });

  const fetchSearchConsoleSites = useMutation({
    mutationFn: async () => {
      if (!brandId) throw new Error("Brand is required");
      const response = await dataChatAPI.getSearchConsoleSites({
        brandId,
      });
      return { sites: response.sites };
    },
    onSuccess: (response) => {
      setGscSites(response.sites);
      const firstSelectableSite = response.sites.find(canUseSearchConsoleSite);
      setSelectedSiteUrl(firstSelectableSite?.siteUrl || "");
      setGoogleOAuthDialog(null);
      setGoogleOAuthUrl("");
      setSiteDialogOpen(true);
    },
    onError: () => {
      toast.error(t("settings.integrations.gsc.connectFailed"));
    },
  });

  const fetchGoogleAnalyticsProperties = useMutation({
    mutationFn: async () => {
      if (!brandId) throw new Error("Brand is required");
      const response = await dataChatAPI.getGoogleAnalyticsProperties({
        brandId,
      });
      return { properties: response.properties };
    },
    onSuccess: (response) => {
      setGa4Properties(response.properties);
      const firstProperty = response.properties[0];
      setSelectedGa4AccountName(
        firstProperty ? ga4AccountKey(firstProperty) : "",
      );
      setSelectedGa4PropertyId(firstProperty?.propertyId || "");
      setGoogleOAuthDialog(null);
      setGoogleOAuthUrl("");
      setGa4DialogOpen(true);
    },
    onError: () => {
      toast.error(t("settings.integrations.ga4.connectFailed"));
    },
  });

  const startGoogleOAuthConnect = useMutation({
    mutationFn: async (provider: GoogleConnectProvider) => {
      if (!brandId) throw new Error("Brand is required");
      return dataChatAPI.getGoogleOAuthConnectUrl({ brandId, provider });
    },
    onSuccess: (response, provider) => {
      setGoogleOAuthUrl(response.url);
      setGoogleOAuthDialog({ provider, step: "grant" });
      const oauthWindow = googleOAuthWindowRef.current;
      if (oauthWindow && !oauthWindow.closed) {
        oauthWindow.location.href = response.url;
        return;
      }
      window.open(response.url, "_blank", "noopener,noreferrer");
    },
    onError: () => {
      const oauthWindow = googleOAuthWindowRef.current;
      if (oauthWindow && !oauthWindow.closed) {
        oauthWindow.close();
      }
      toast.error(t("settings.integrations.oauth.connectFailed"));
    },
  });

  const selectSearchConsoleSite = useMutation({
    mutationFn: async (siteUrl: string) => {
      if (!brandId) throw new Error("Brand is required");
      return dataChatAPI.syncSearchConsole({
        brandId,
        siteUrl,
      });
    },
    onSuccess: async () => {
      toast.success(t("settings.integrations.gsc.connected"));
      setSiteDialogOpen(false);
      setSelectedSiteUrl("");
      setGscSites([]);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: sourcesQueryKey }),
      ]);
    },
    onError: () => {
      toast.error(t("settings.integrations.gsc.selectFailed"));
    },
  });

  const selectGoogleAnalyticsProperty = useMutation({
    mutationFn: async (propertyId: string) => {
      if (!brandId) throw new Error("Brand is required");
      return dataChatAPI.syncGoogleAnalytics({
        brandId,
        propertyId,
      });
    },
    onSuccess: async () => {
      toast.success(t("settings.integrations.ga4.connected"));
      setGa4DialogOpen(false);
      setSelectedGa4PropertyId("");
      setSelectedGa4AccountName("");
      setGa4Properties([]);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: sourcesQueryKey }),
      ]);
    },
    onError: () => {
      toast.error(t("settings.integrations.ga4.selectFailed"));
    },
  });

  const disconnectGoogleIntegration = useMutation({
    mutationFn: async (provider: GoogleConnectProvider) => {
      if (!brandId) throw new Error("Brand is required");
      if (provider === "search_console") {
        return dataChatAPI.disconnectSearchConsole({ brandId });
      }
      return dataChatAPI.disconnectGoogleAnalytics({ brandId });
    },
    onSuccess: async (_response, provider) => {
      toast.success(
        t(
          provider === "ga4"
            ? "settings.integrations.ga4.disconnected"
            : "settings.integrations.gsc.disconnected",
        ),
      );
      setDisconnectDialog(null);
      if (provider === "search_console") {
        setSiteDialogOpen(false);
        setSelectedSiteUrl("");
        setGscSites([]);
      } else {
        setGa4DialogOpen(false);
        setSelectedGa4PropertyId("");
        setSelectedGa4AccountName("");
        setGa4Properties([]);
      }
      queryClient.setQueryData<DataChatSource[]>(
        sourcesQueryKey,
        (currentSources) =>
          currentSources?.map((source) =>
            source.provider === provider
              ? {
                  ...source,
                  status: "disconnected",
                  propertyName: null,
                  connectedAt: null,
                  authMode: null,
                  siteSelectionRequired: false,
                  propertySelectionRequired: false,
                  availableInChat: false,
                }
              : source,
          ),
      );
      await queryClient.invalidateQueries({ queryKey: sourcesQueryKey });
    },
    onError: (_error, provider) => {
      toast.error(
        t(
          provider === "ga4"
            ? "settings.integrations.ga4.disconnectFailed"
            : "settings.integrations.gsc.disconnectFailed",
        ),
      );
    },
  });

  useEffect(() => {
    capturePosthogEvent("integrations_page_opened", analyticsContext);
  }, [analyticsContext]);

  useEffect(() => {
    if (!brandId || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("googleOAuth") !== "connected") return;
    const oauthBrandId = params.get("brandId");
    const provider = params.get("provider");
    if (oauthBrandId && oauthBrandId !== brandId) return;
    if (provider !== "search_console" && provider !== "ga4") return;
    const fingerprint = `${provider}:${oauthBrandId || brandId}`;
    if (processedOAuthReturnRef.current === fingerprint) return;
    processedOAuthReturnRef.current = fingerprint;
    params.delete("googleOAuth");
    params.delete("provider");
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`,
    );
    if (provider === "search_console") {
      fetchSearchConsoleSites.mutate();
      return;
    }
    fetchGoogleAnalyticsProperties.mutate();
  }, [brandId, fetchGoogleAnalyticsProperties, fetchSearchConsoleSites]);

  useEffect(() => {
    if (!selectedSiteUrl && gscSites.length) {
      const firstSelectableSite = gscSites.find(canUseSearchConsoleSite);
      setSelectedSiteUrl(firstSelectableSite?.siteUrl || "");
    }
  }, [gscSites, selectedSiteUrl]);

  useEffect(() => {
    const accountProperties = selectedGa4AccountName
      ? ga4Properties.filter(
          (property) => ga4AccountKey(property) === selectedGa4AccountName,
        )
      : ga4Properties;
    if (
      selectedGa4PropertyId &&
      accountProperties.some(
        (property) => property.propertyId === selectedGa4PropertyId,
      )
    ) {
      return;
    }
    setSelectedGa4PropertyId(accountProperties[0]?.propertyId || "");
  }, [ga4Properties, selectedGa4AccountName, selectedGa4PropertyId]);

  const handleSiteDialogOpenChange = (open: boolean) => {
    setSiteDialogOpen(open);
    if (!open) {
      setSelectedSiteUrl("");
      setGscSites([]);
    }
  };

  const handleGa4DialogOpenChange = (open: boolean) => {
    setGa4DialogOpen(open);
    if (!open) {
      setSelectedGa4PropertyId("");
      setSelectedGa4AccountName("");
      setGa4Properties([]);
    }
  };

  const sourceByProvider = useMemo(() => {
    return new Map<DataSourceProvider, DataChatSource>(
      sources.map((source) => [source.provider, source]),
    );
  }, [sources]);

  const ga4Accounts = useMemo(() => {
    const accounts = new Map<string, string>();
    ga4Properties.forEach((property) => {
      const accountKey = ga4AccountKey(property);
      if (!accounts.has(accountKey)) {
        accounts.set(
          accountKey,
          property.accountDisplayName ||
            property.accountName ||
            t("settings.integrations.ga4.defaultAccount"),
        );
      }
    });
    return Array.from(accounts, ([value, label]) => ({ value, label }));
  }, [ga4Properties, t]);

  const selectedGa4AccountProperties = useMemo(() => {
    if (!selectedGa4AccountName) return ga4Properties;
    return ga4Properties.filter((property) => {
      return ga4AccountKey(property) === selectedGa4AccountName;
    });
  }, [ga4Properties, selectedGa4AccountName]);

  const cards = useMemo<IntegrationCard[]>(() => {
    const googleCards = CONNECTABLE_PROVIDERS.map((provider) => {
      const source = sourceByProvider.get(provider);
      const isGa4 = provider === "ga4";

      return {
        provider,
        name: source?.label || googleIntegrationName(provider, t),
        description:
          source?.description ||
          (isGa4
            ? t("settings.integrations.ga4.description")
            : t("settings.integrations.gsc.description")),
        state: sourceState(source),
        source,
        icon: googleIcon(provider),
        iconClassName: isGa4 ? "text-[#F9AB00]" : "text-[#4285F4]",
        iconTileClassName: "bg-white border border-[#E5E5E5]",
        propertyName: source?.propertyName,
      };
    });

    return [
      ...googleCards,
      {
        provider: "webflow",
        name: "Webflow",
        description: t("settings.integrations.webflow.description"),
        state: "soon",
        icon: <span className="text-base font-bold leading-none">W</span>,
        iconClassName: "text-white",
        iconTileClassName: "bg-[#146EF5]",
      },
      {
        provider: "notion",
        name: "Notion",
        description: t("settings.integrations.notion.description"),
        state: "soon",
        icon: <span className="text-lg font-black leading-none">N</span>,
        iconClassName: "text-[#0A0A0A]",
        iconTileClassName: "bg-white border border-[#E5E5E5]",
      },
      {
        provider: "github",
        name: "GitHub",
        description: t("settings.integrations.github.description"),
        state: "soon",
        icon: <Github className="h-5 w-5" />,
        iconClassName: "text-white",
        iconTileClassName: "bg-[#0A0A0A]",
      },
    ];
  }, [sourceByProvider, t]);

  const filteredCards = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return cards.filter((card) => {
      const matchesSearch =
        !normalizedSearch ||
        card.name.toLowerCase().includes(normalizedSearch) ||
        card.description.toLowerCase().includes(normalizedSearch);

      return matchesSearch && cardMatchesFilter(card, filter);
    });
  }, [cards, filter, searchQuery]);

  function selectFilter(nextFilter: IntegrationFilter) {
    setFilter(nextFilter);
    capturePosthogEvent("integration_filter_changed", {
      ...analyticsContext,
      filter: nextFilter,
    });
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value);
    if (value.trim()) {
      capturePosthogEvent("integration_search_used", {
        ...analyticsContext,
        query_length: value.trim().length,
      });
    }
  }

  function handleGa4AccountChange(accountName: string) {
    setSelectedGa4AccountName(accountName);
    const firstProperty = ga4Properties.find((property) => {
      return ga4AccountKey(property) === accountName;
    });
    setSelectedGa4PropertyId(firstProperty?.propertyId || "");
  }

  function openGoogleOAuthDialog(provider: GoogleConnectProvider) {
    setGoogleOAuthDialog({ provider, step: "connect" });
    setGoogleOAuthUrl("");
  }

  function handleGoogleOAuthDialogOpenChange(open: boolean) {
    if (open) return;
    setGoogleOAuthDialog(null);
    setGoogleOAuthUrl("");
  }

  function handleContinueToGoogle() {
    if (!googleOAuthDialog) return;
    const oauthWindow = window.open("about:blank", "_blank");
    if (oauthWindow) {
      oauthWindow.opener = null;
    }
    googleOAuthWindowRef.current = oauthWindow;
    startGoogleOAuthConnect.mutate(googleOAuthDialog.provider);
  }

  function handleReopenGoogleTab() {
    if (!googleOAuthUrl) return;
    window.open(googleOAuthUrl, "_blank", "noopener,noreferrer");
  }

  async function handleGoogleOAuthCompleted() {
    if (!googleOAuthDialog) return;
    if (!brandId) return;
    const response = await dataChatAPI.getSources(brandId);
    queryClient.setQueryData(sourcesQueryKey, response.sources);
    const source = response.sources.find(
      (item) => item.provider === googleOAuthDialog.provider,
    );
    const isReadyForSelection =
      hasUsableGoogleGrant(source) &&
      (source?.siteSelectionRequired || source?.propertySelectionRequired);

    if (!isReadyForSelection) {
      toast.error(t("settings.integrations.oauth.connectFailed"));
      return;
    }

    if (googleOAuthDialog.provider === "search_console") {
      fetchSearchConsoleSites.mutate();
      return;
    }
    fetchGoogleAnalyticsProperties.mutate();
  }

  function handleAction(card: IntegrationCard) {
    capturePosthogEvent(
      card.state === "connected"
        ? "integration_manage_clicked"
        : "integration_connect_clicked",
      {
        ...analyticsContext,
        provider: card.provider,
        status: card.source?.status || card.state,
        available_in_chat: card.source?.availableInChat,
      },
    );

    if (card.provider === "search_console" || card.provider === "ga4") {
      if (hasUsableGoogleGrant(card.source)) {
        if (card.provider === "search_console") {
          fetchSearchConsoleSites.mutate();
        } else {
          fetchGoogleAnalyticsProperties.mutate();
        }
        return;
      }
      openGoogleOAuthDialog(card.provider);
    }
  }

  function handleReconnectAction(card: IntegrationCard) {
    if (card.provider !== "search_console" && card.provider !== "ga4") return;
    capturePosthogEvent("integration_reconnect_clicked", {
      ...analyticsContext,
      provider: card.provider,
      status: card.source?.status || card.state,
      available_in_chat: card.source?.availableInChat,
    });
    openGoogleOAuthDialog(card.provider);
  }

  function handleDisconnectAction(card: IntegrationCard) {
    if (card.provider !== "search_console" && card.provider !== "ga4") return;
    capturePosthogEvent("integration_disconnect_clicked", {
      ...analyticsContext,
      provider: card.provider,
      status: card.source?.status || card.state,
      available_in_chat: card.source?.availableInChat,
    });
    setDisconnectDialog({ provider: card.provider, name: card.name });
  }

  function confirmDisconnectIntegration() {
    if (!disconnectDialog) return;
    disconnectGoogleIntegration.mutate(disconnectDialog.provider);
  }

  const googleOAuthProvider = googleOAuthDialog?.provider;
  const googleOAuthProviderName = googleOAuthProvider
    ? googleIntegrationName(googleOAuthProvider, t)
    : "";
  const googleOAuthProviderIcon = googleOAuthProvider
    ? googleIcon(googleOAuthProvider, "h-5 w-5")
    : null;
  const googleOAuthIconClassName =
    googleOAuthProvider === "ga4" ? "text-[#F9AB00]" : "text-[#4285F4]";
  const googleOAuthSelectionPending =
    googleOAuthProvider === "search_console"
      ? fetchSearchConsoleSites.isPending
      : fetchGoogleAnalyticsProperties.isPending;
  const selectedSite = gscSites.find(
    (site) => site.siteUrl === selectedSiteUrl,
  );

  return (
    <>
      <div
        className={
          embedded ? "bg-white" : "h-full overflow-y-auto bg-white p-4 md:p-6"
        }
      >
        <div className={embedded ? "" : "mx-auto max-w-[900px]"}>
          <div className="mb-7">
            <h1 className="text-lg font-semibold text-[#171717]">
              {t("settings.integrations.title")}
            </h1>
            <p className="mt-1 max-w-[720px] text-sm leading-6 text-[#737373]">
              {t("settings.integrations.subtitle")}
            </p>
          </div>

          <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div
              className="inline-flex w-fit rounded-[8px] bg-[#F5F5F5] p-1"
              role="tablist"
              aria-label={t("settings.integrations.filterLabel")}
            >
              {FILTERS.map((option) => {
                const isActive = filter === option;

                return (
                  <button
                    key={option}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={cn(
                      "h-8 rounded-[7px] px-4 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-white text-[#171717] shadow-sm"
                        : "text-[#737373] hover:text-[#171717]",
                    )}
                    onClick={() => selectFilter(option)}
                  >
                    {t(`settings.integrations.filter.${option}`)}
                  </button>
                );
              })}
            </div>

            <div className="relative w-full md:w-[280px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#171717]" />
              <Input
                value={searchQuery}
                onChange={(event) => handleSearchChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
                placeholder={t("settings.integrations.searchPlaceholder")}
                className="h-9 rounded-[8px] border-[#DDDDDD] bg-white pl-9 text-sm shadow-none"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="h-[136px] animate-pulse rounded-[8px] border border-[#E5E5E5] bg-[#FAFAFA]"
                />
              ))}
            </div>
          ) : filteredCards.length ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {filteredCards.map((card) => {
                const action = actionCopy(card, t);
                const isConnected = card.state === "connected";
                const isActionRequired = card.state === "action_required";
                const canReconnect =
                  isConnected &&
                  (card.provider === "search_console" ||
                    card.provider === "ga4");
                const canDisconnect =
                  Boolean(card.source) &&
                  (card.provider === "search_console" ||
                    card.provider === "ga4") &&
                  card.source?.status !== "disconnected";
                const isActionBusy =
                  (card.provider === "search_console" &&
                    fetchSearchConsoleSites.isPending) ||
                  (card.provider === "ga4" &&
                    fetchGoogleAnalyticsProperties.isPending) ||
                  ((card.provider === "search_console" ||
                    card.provider === "ga4") &&
                    (startGoogleOAuthConnect.isPending ||
                      disconnectGoogleIntegration.isPending));

                return (
                  <section
                    key={card.provider}
                    className={cn(
                      "min-h-[136px] rounded-[8px] border border-[#E5E5E5] bg-white p-5 transition-colors",
                      card.state !== "soon" && "hover:border-[#CFCFCF]",
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex min-w-0 items-center gap-4">
                        <div
                          className={cn(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px]",
                            card.iconTileClassName,
                            card.iconClassName,
                          )}
                          aria-hidden="true"
                        >
                          {card.icon}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-sm font-semibold text-[#171717]">
                              {card.name}
                            </h2>
                            {card.badge ? (
                              <Badge
                                variant="outline"
                                className={cn(
                                  "h-6 rounded-[7px] border-transparent px-2 text-xs font-medium",
                                  card.state === "soon"
                                    ? "bg-[#F5F5F5] text-[#171717]"
                                    : "bg-orange-50 text-orange-600",
                                )}
                              >
                                {card.badge}
                              </Badge>
                            ) : null}
                            {isConnected ? (
                              <span
                                className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-emerald-500 text-emerald-600"
                                aria-label={t(
                                  "settings.integrations.status.connected",
                                )}
                              >
                                <Check className="h-3 w-3" />
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {card.state === "soon" ? (
                        <Badge className="h-6 rounded-[7px] border-transparent bg-[#F5F5F5] px-2 text-xs font-medium text-[#171717]">
                          {action.label}
                        </Badge>
                      ) : (
                        <div className="flex shrink-0 items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="secondary"
                                  aria-label={action.label}
                                  disabled={action.disabled || isActionBusy}
                                  className="h-8 w-8 rounded-[8px] bg-[#F5F5F5] text-[#171717] shadow-none hover:bg-[#EEEEEE] disabled:opacity-60"
                                  onClick={() => handleAction(card)}
                                >
                                  {isActionBusy ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    action.icon
                                  )}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent sideOffset={8}>
                              {action.tooltip}
                            </TooltipContent>
                          </Tooltip>
                          {canReconnect ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="secondary"
                                    aria-label={t(
                                      "settings.integrations.action.reconnect",
                                    )}
                                    disabled={startGoogleOAuthConnect.isPending}
                                    className="h-8 w-8 rounded-[8px] bg-[#F5F5F5] text-[#171717] shadow-none hover:bg-[#EEEEEE] disabled:opacity-60"
                                    onClick={() => handleReconnectAction(card)}
                                  >
                                    {startGoogleOAuthConnect.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <RefreshCw className="h-4 w-4" />
                                    )}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent sideOffset={8}>
                                {t("settings.integrations.tooltip.reconnect")}
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                          {canDisconnect ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="secondary"
                                    aria-label={t(
                                      "settings.integrations.action.disconnect",
                                    )}
                                    disabled={
                                      disconnectGoogleIntegration.isPending
                                    }
                                    className="h-8 w-8 rounded-[8px] bg-[#F5F5F5] text-[#B42318] shadow-none hover:bg-[#FEE4E2] disabled:opacity-60"
                                    onClick={() => handleDisconnectAction(card)}
                                  >
                                    {disconnectGoogleIntegration.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Unplug className="h-4 w-4" />
                                    )}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent sideOffset={8}>
                                {t("settings.integrations.tooltip.disconnect")}
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                        </div>
                      )}
                    </div>

                    <p className="mt-5 text-sm leading-5 text-[#737373]">
                      {card.description}
                    </p>

                    {card.propertyName || isActionRequired ? (
                      <p
                        className={cn(
                          "mt-3 text-xs leading-5",
                          isActionRequired
                            ? "text-amber-700"
                            : "text-[#737373]",
                        )}
                      >
                        {isActionRequired
                          ? t("settings.integrations.status.actionRequired")
                          : t("settings.integrations.property", {
                              propertyName: card.propertyName || "",
                            })}
                      </p>
                    ) : null}
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="rounded-[8px] border border-dashed border-[#DDDDDD] bg-[#FAFAFA] p-8 text-center">
              <p className="text-sm font-medium text-[#171717]">
                {t("settings.integrations.empty.title")}
              </p>
              <p className="mt-1 text-sm text-[#737373]">
                {t("settings.integrations.empty.subtitle")}
              </p>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={Boolean(googleOAuthDialog)}
        onOpenChange={handleGoogleOAuthDialogOpenChange}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-[360px] gap-5 rounded-[8px] p-5"
        >
          <DialogHeader className="items-center gap-3 text-center sm:text-center">
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-[8px] border border-[#E5E5E5] bg-white",
                googleOAuthIconClassName,
              )}
              aria-hidden="true"
            >
              {googleOAuthProviderIcon}
            </div>
            <div>
              <DialogTitle className="text-base leading-6 text-[#171717]">
                {googleOAuthDialog?.step === "grant"
                  ? t("settings.integrations.oauth.grantTitle")
                  : t(
                      googleOAuthProvider === "ga4"
                        ? "settings.integrations.ga4.connectTitle"
                        : "settings.integrations.gsc.connectTitle",
                    )}
              </DialogTitle>
              <DialogDescription className="mt-2 text-xs leading-5 text-[#737373]">
                {googleOAuthDialog?.step === "grant"
                  ? t("settings.integrations.oauth.grantDescription")
                  : t(
                      googleOAuthProvider === "ga4"
                        ? "settings.integrations.ga4.connectDescription"
                        : "settings.integrations.gsc.connectDescription",
                    )}
              </DialogDescription>
            </div>
          </DialogHeader>

          {googleOAuthDialog?.step === "connect" ? (
            <p className="text-center text-[11px] leading-4 text-[#737373]">
              {t("settings.integrations.oauth.readOnlyNote")} ·{" "}
              {t("settings.integrations.oauth.disconnectAnytime")}
            </p>
          ) : (
            <div className="space-y-3 text-center">
              <p className="text-xs text-[#737373]">
                {t("settings.integrations.oauth.didntWork")}{" "}
                <button
                  type="button"
                  className="font-medium text-[#F25F29] underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={!googleOAuthUrl}
                  onClick={handleReopenGoogleTab}
                >
                  {t("settings.integrations.oauth.reopenTab")}
                </button>
              </p>
              <p className="text-[11px] leading-4 text-[#737373]">
                {t("settings.integrations.oauth.providerHint", {
                  providerName: googleOAuthProviderName,
                })}
              </p>
            </div>
          )}

          <DialogFooter className="flex-row gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-[8px] shadow-none"
              onClick={() => handleGoogleOAuthDialogOpenChange(false)}
            >
              {t("settings.integrations.action.cancel")}
            </Button>
            {googleOAuthDialog?.step === "grant" ? (
              <Button
                type="button"
                className="h-9 rounded-[8px] bg-[#F25F29] px-3 text-white shadow-none hover:bg-[#E6531F]"
                disabled={googleOAuthSelectionPending}
                onClick={handleGoogleOAuthCompleted}
              >
                {googleOAuthSelectionPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {t("settings.integrations.oauth.completedSteps")}
              </Button>
            ) : (
              <Button
                type="button"
                className="h-9 rounded-[8px] bg-[#F25F29] px-3 text-white shadow-none hover:bg-[#E6531F]"
                disabled={startGoogleOAuthConnect.isPending}
                onClick={handleContinueToGoogle}
              >
                {startGoogleOAuthConnect.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                {t("settings.integrations.oauth.continueToGoogle")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(disconnectDialog)}
        onOpenChange={(open) => {
          if (!open && !disconnectGoogleIntegration.isPending) {
            setDisconnectDialog(null);
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-[360px] gap-5 rounded-[8px] p-5"
        >
          <DialogHeader className="items-center gap-3 text-center sm:text-center">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-[#FEE4E2] text-[#B42318]"
              aria-hidden="true"
            >
              <Unplug className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-base leading-6 text-[#171717]">
                {t("settings.integrations.disconnect.title", {
                  integrationName: disconnectDialog?.name || "",
                })}
              </DialogTitle>
              <DialogDescription className="mt-2 text-xs leading-5 text-[#737373]">
                {t("settings.integrations.disconnect.description")}
              </DialogDescription>
            </div>
          </DialogHeader>

          <DialogFooter className="flex-row gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-[8px] shadow-none"
              disabled={disconnectGoogleIntegration.isPending}
              onClick={() => setDisconnectDialog(null)}
            >
              {t("settings.integrations.action.cancel")}
            </Button>
            <Button
              type="button"
              className="h-9 rounded-[8px] bg-[#B42318] px-3 text-white shadow-none hover:bg-[#912018]"
              disabled={disconnectGoogleIntegration.isPending}
              onClick={confirmDisconnectIntegration}
            >
              {disconnectGoogleIntegration.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Unplug className="h-4 w-4" />
              )}
              {t("settings.integrations.action.disconnect")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={siteDialogOpen} onOpenChange={handleSiteDialogOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="max-w-[380px] gap-5 rounded-[8px] p-5"
        >
          <DialogHeader className="items-center text-center sm:text-center">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-[#E5E5E5] bg-white text-[#4285F4]"
              aria-hidden="true"
            >
              <Search className="h-5 w-5" />
            </div>
            <DialogTitle className="text-base leading-6 text-[#171717]">
              {t("settings.integrations.gsc.selectTitle")}
            </DialogTitle>
            <DialogDescription className="text-xs leading-5 text-[#737373]">
              {t("settings.integrations.gsc.selectDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[320px] overflow-y-auto">
            {fetchSearchConsoleSites.isPending ? (
              <div className="flex h-24 items-center justify-center text-sm text-[#737373]">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("settings.integrations.gsc.loadingSites")}
              </div>
            ) : gscSites.length ? (
              <RadioGroup
                value={selectedSiteUrl}
                onValueChange={setSelectedSiteUrl}
                className="gap-0 rounded-[8px] border border-[#E5E5E5]"
              >
                {gscSites.map((site) => {
                  const canUseSite = canUseSearchConsoleSite(site);
                  return (
                    <label
                      key={site.siteUrl}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 border-b border-[#E5E5E5] px-3 py-3 last:border-b-0",
                        !canUseSite &&
                          "cursor-not-allowed bg-[#FAFAFA] opacity-70",
                      )}
                    >
                      <RadioGroupItem
                        value={site.siteUrl}
                        disabled={!canUseSite}
                        className="mt-0.5 border-[#DDDDDD] text-[#F25F29]"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-[#171717]">
                          {site.siteUrl}
                        </span>
                        {site.permissionLevel ? (
                          <span className="mt-1 block text-xs text-[#737373]">
                            {canUseSite
                              ? site.permissionLevel
                              : t("settings.integrations.gsc.unverifiedSite")}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </RadioGroup>
            ) : (
              <div className="rounded-[8px] border border-dashed border-[#DDDDDD] p-4 text-sm text-[#737373]">
                {t("settings.integrations.gsc.noSites")}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-[8px] shadow-none"
              onClick={() => handleSiteDialogOpenChange(false)}
            >
              {t("settings.integrations.action.cancel")}
            </Button>
            <Button
              type="button"
              className="h-9 rounded-[8px] bg-[#F25F29] text-white shadow-none hover:bg-[#E6531F]"
              disabled={
                !selectedSiteUrl ||
                !selectedSite ||
                !canUseSearchConsoleSite(selectedSite) ||
                selectSearchConsoleSite.isPending
              }
              onClick={() => selectSearchConsoleSite.mutate(selectedSiteUrl)}
            >
              {selectSearchConsoleSite.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {t("settings.integrations.gsc.connectSite")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ga4DialogOpen} onOpenChange={handleGa4DialogOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="max-w-[380px] gap-5 rounded-[8px] p-5"
        >
          <DialogHeader className="items-center text-center sm:text-center">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-[#E5E5E5] bg-white text-[#F9AB00]"
              aria-hidden="true"
            >
              <BarChart3 className="h-5 w-5" />
            </div>
            <DialogTitle className="text-base leading-6 text-[#171717]">
              {t("settings.integrations.ga4.selectTitle")}
            </DialogTitle>
            <DialogDescription className="text-xs leading-5 text-[#737373]">
              {t("settings.integrations.ga4.selectDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[360px] space-y-3 overflow-y-auto">
            {fetchGoogleAnalyticsProperties.isPending ? (
              <div className="flex h-24 items-center justify-center text-sm text-[#737373]">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("settings.integrations.ga4.loadingProperties")}
              </div>
            ) : ga4Properties.length ? (
              <>
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-[#171717]">
                    {t("settings.integrations.ga4.accountLabel")}
                  </p>
                  <Select
                    value={selectedGa4AccountName}
                    onValueChange={handleGa4AccountChange}
                  >
                    <SelectTrigger className="h-9 w-full rounded-[8px] border-[#DDDDDD] bg-white shadow-none">
                      <SelectValue
                        placeholder={t(
                          "settings.integrations.ga4.accountPlaceholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {ga4Accounts.map((account) => (
                        <SelectItem key={account.value} value={account.value}>
                          {account.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-[#171717]">
                    {t("settings.integrations.ga4.propertyLabel")}
                  </p>
                  <RadioGroup
                    value={selectedGa4PropertyId}
                    onValueChange={setSelectedGa4PropertyId}
                    className="gap-0 rounded-[8px] border border-[#E5E5E5]"
                  >
                    {selectedGa4AccountProperties.map((property) => (
                      <label
                        key={property.propertyId}
                        className="flex cursor-pointer items-start gap-3 border-b border-[#E5E5E5] px-3 py-3 last:border-b-0"
                      >
                        <RadioGroupItem
                          value={property.propertyId}
                          className="mt-0.5 border-[#DDDDDD] text-[#F25F29]"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-[#171717]">
                            {property.displayName}
                          </span>
                          <span className="mt-1 block truncate text-xs text-[#737373]">
                            {property.propertyId}
                          </span>
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <p className="text-[11px] leading-4 text-[#737373]">
                  {t("settings.integrations.ga4.selectionHint")}
                </p>
              </>
            ) : (
              <div className="rounded-[8px] border border-dashed border-[#DDDDDD] p-4 text-sm text-[#737373]">
                {t("settings.integrations.ga4.noProperties")}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-[8px] shadow-none"
              onClick={() => handleGa4DialogOpenChange(false)}
            >
              {t("settings.integrations.action.cancel")}
            </Button>
            <Button
              type="button"
              className="h-9 rounded-[8px] bg-[#F25F29] text-white shadow-none hover:bg-[#E6531F]"
              disabled={
                !selectedGa4PropertyId ||
                selectGoogleAnalyticsProperty.isPending
              }
              onClick={() =>
                selectGoogleAnalyticsProperty.mutate(selectedGa4PropertyId)
              }
            >
              {selectGoogleAnalyticsProperty.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {t("settings.integrations.ga4.connectProperty")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
